/**
 * Shared email variable configuration.
 *
 * Single source of truth for available template variables in email sequences.
 * Used by the email body editor, sequence preview, and review emails step.
 */

// Mirrors the user-pickable subset of
// server/emailbison/usecases/variable_resolution_utils.py AVAILABLE_VARIABLES.
// NOTE: the backend set additionally includes LOCATION, which is intentionally
// excluded here — it's consumed only by the geography_classifier engine and
// is not surfaced in the user-facing variable picker for the POC.
export const AVAILABLE_VARIABLES = [
  { value: '{FIRST_NAME}', label: 'First Name', token: 'FIRST_NAME' },
  { value: '{LAST_NAME}', label: 'Last Name', token: 'LAST_NAME' },
  { value: '{EMAIL}', label: 'Email', token: 'EMAIL' },
  { value: '{COMPANY}', label: 'Company', token: 'COMPANY' },
  { value: '{TITLE}', label: 'Title', token: 'TITLE' },
] as const

export type VariableToken = (typeof AVAILABLE_VARIABLES)[number]['token']

// DS2026 styling for the Variables picker menu. Overrides the legacy base-dropdown
// chrome (rounded-md / shadow-md / surface-muted, text-md rows with sage hover) so
// the menu matches the DS2026 filter popovers. Shared by the Build (EmailBodyEditor)
// and Review (OverrideBodyEditor) editors so the two pickers stay identical.
export const VARIABLE_MENU_CONTENT_CLASS =
  'rounded-xl border-neutral-200 bg-white p-1.5 shadow-lg'
// Override the dropdown base's green sage hover/focus with a neutral DS2026
// gray so the variable picker matches the rest of the new system.
export const VARIABLE_MENU_ITEM_CLASS =
  'rounded-lg text-sm hover:bg-neutral-100 focus:bg-neutral-100 data-[highlighted]:bg-neutral-100'

/**
 * Replace {VARIABLE} tokens in text with values from the token map.
 *
 * @param text - Template text with {VARIABLE} placeholders
 * @param tokens - Map of variable name to resolved value
 * @param options.escapeValues - If true, HTML-escape values before substitution
 */
export function fillVariables(
  text: string,
  tokens: Record<string, string>,
  options?: { escapeValues?: boolean }
): string {
  return text.replace(/\{([A-Za-z_][A-Za-z0-9_]*)\}/g, (match, key) => {
    const value = tokens[key]
    if (value === undefined) return match
    const replacement = options?.escapeValues ? escapeHtml(value) : value
    return replacement
  })
}

/**
 * Find any {variable_name} placeholders in text that haven't been resolved.
 * This deliberately catches malformed placeholders too, since `{123}` and
 * `{MENU-ITEM}` cannot be safely filled at send time.
 */
export function detectUnfilledVariables(text: string): string[] {
  return Array.from(new Set(text.match(/\{[^{}]+\}/g) ?? []))
}

/** Structural shape of a contact used to resolve standard email variables. */
export interface ContactTokenSource {
  full_name?: string | null
  email?: string | null
  company_name?: string | null
  title?: string | null
}

/**
 * Coalesce a contact name to a usable greeting value, defaulting to "there"
 * when blank. Mirrors the server's `coalesce_contact_name` (FIR-7704):
 * whitespace-only falls back to "there"; an existing name is returned verbatim
 * (case preserved). Do NOT title-case here -- the server send/render path
 * preserves stored casing too, and re-casing would mangle names like
 * "McDonald" -> "Mcdonald" in the preview vs. what actually sends.
 *
 * Server twin: server/contacts/services/contact_name.py::coalesce_contact_name
 * (DEFAULT_CONTACT_NAME) -- keep the value and the case-preserving behavior in sync.
 */
export const DEFAULT_CONTACT_NAME = 'there'

export function coalesceContactName(value: string | null | undefined): string {
  return (value ?? '').trim() || DEFAULT_CONTACT_NAME
}

/**
 * Split a contact's `full_name` into first and last name parts: the first
 * whitespace-delimited token is the first name, the remainder is the last name.
 * Centralizes the split so the token map and the preview label can't drift.
 */
export function parseFullName(fullName: string | null | undefined): {
  firstName: string
  lastName: string
} {
  const [firstName = '', ...rest] = (fullName ?? '')
    .trim()
    .split(' ')
    .filter(Boolean)
  return { firstName, lastName: rest.join(' ') }
}

/**
 * Build the standard {VARIABLE} token map from a contact's fields.
 *
 * FIRST_NAME/LAST_NAME always resolve, coalescing to "there" when blank
 * (FIR-7704), so the builder preview and the Review/Launch checks match what
 * the server actually sends. The other standard vars stay omitted-when-empty
 * (mirroring the server's `include_empty_standard=False`), so a contact with no
 * COMPANY still leaves `{COMPANY}` unresolved and Launch stays blocked on it.
 * Shared by the builder preview, Review Emails, and Launch steps so the checks
 * cannot drift apart.
 */
export function buildContactTokens(
  contact: ContactTokenSource
): Record<string, string> {
  const { firstName, lastName } = parseFullName(contact.full_name)
  const tokens: Record<string, string> = {
    FIRST_NAME: coalesceContactName(firstName),
    LAST_NAME: coalesceContactName(lastName),
  }
  if (contact.email) tokens.EMAIL = contact.email
  if (contact.company_name) tokens.COMPANY = contact.company_name
  if (contact.title) tokens.TITLE = contact.title
  return tokens
}

/**
 * Human-readable identifier for a contact in validation messages.
 *
 * Falls back to the email (then a generic label) when the contact has no name,
 * mirroring the server's `_contact_display_name`, so "First issue:" never
 * renders blank for an email-only contact (FIR-7525).
 */
export function contactDisplayName(contact: {
  full_name?: string | null
  email?: string | null
}): string {
  return (contact.full_name || '').trim() || contact.email || 'this contact'
}

/** Count distinct contacts with at least one unfilled-variable email. */
export function countContactsWithUnfilledVariables<
  T extends { contact: { contact_id: number } },
>(issues: ReadonlyArray<T>): number {
  return new Set(issues.map((issue) => issue.contact.contact_id)).size
}

export function findUnknownTemplateVariables(
  variables: ReadonlyArray<EmailVariable>,
  texts: ReadonlyArray<string | null | undefined>
): string[] {
  const knownTokens = new Set(variables.map((variable) => variable.token))
  const foundTokens = texts.flatMap((text) =>
    typeof text === 'string' ? detectUnfilledVariables(text) : []
  )
  return Array.from(
    new Set(
      foundTokens.filter((token) => {
        const tokenName = token.replace(/[{}]/g, '')
        return !knownTokens.has(tokenName)
      })
    )
  )
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

/**
 * Shape of a single entry in the email variable picker. This covers both
 * built-in `AVAILABLE_VARIABLES` items and campaign-scoped AI/manual columns.
 */
export interface EmailVariable {
  value: string // e.g. '{SNIPPED_COMPANY}'
  label: string // human-readable column name
  token: string // e.g. 'SNIPPED_COMPANY'
  isAI?: boolean // true for campaign-scoped AI columns
  isCampaignColumn?: boolean // true for campaign-scoped custom/manual columns
  isManual?: boolean
  missingCount?: number
  totalCount?: number
  hasMissingValues?: boolean
}

/**
 * Merge built-in variables with campaign-specific column variables.
 * Built-ins come first, then custom in definition order.
 */
export function mergeVariables(
  custom: ReadonlyArray<EmailVariable> = []
): ReadonlyArray<EmailVariable> {
  return [...AVAILABLE_VARIABLES, ...custom]
}

export function findCampaignColumnVariablesWithMissingValues(
  variables: ReadonlyArray<EmailVariable>,
  texts: ReadonlyArray<string | null | undefined>
): ReadonlyArray<EmailVariable> {
  return variables.filter(
    (variable) =>
      variable.isCampaignColumn &&
      variable.hasMissingValues &&
      texts.some((text) => {
        if (typeof text !== 'string') return false
        return detectUnfilledVariables(text).some(
          (token) => token.replace(/[{}]/g, '') === variable.token
        )
      })
  )
}

export function getMissingValuesLabel(variable: EmailVariable): string {
  if (
    typeof variable.missingCount === 'number' &&
    typeof variable.totalCount === 'number'
  ) {
    return `${variable.missingCount}/${variable.totalCount} missing`
  }
  return 'Missing values'
}

export function getMissingValuesMessage(variable: EmailVariable): string {
  if (
    typeof variable.missingCount === 'number' &&
    typeof variable.totalCount === 'number'
  ) {
    return `${variable.label} is missing values for ${variable.missingCount} of ${variable.totalCount} contacts. Fill those cells before sending.`
  }
  return `${variable.label} is missing values for some contacts. Fill those cells before sending.`
}
